import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainerReactTable from '../../../../../Components/Common/TableContainerReactTable';
import { Link } from "react-router-dom";
import SelectInput from '../../common/SelectInput';
import FormInput from '../../common/FormInput';
import classnames from 'classnames';

export interface GLAccountData {
  id: string;
  glAccountNo: string;
  name: string;
  shortName: string;
  type: string;
}

export interface GLAccountSearchForm {
  searchField: string;
  searchValue: string;
}

interface GLAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (glAccount: GLAccountData) => void;
  title?: string;
}

const defaultSearchForm: GLAccountSearchForm = {
  searchField: 'Name',
  searchValue: ''
};

// Mock GL account data - in real app this would come from API
const mockGLAccounts: GLAccountData[] = [
  {
    id: '1',
    glAccountNo: '1200',
    name: 'Inventory Asset',
    shortName: 'Inventory',
    type: 'Asset'
  },
  {
    id: '2',
    glAccountNo: '1210',
    name: 'Service Assets',
    shortName: 'Service',
    type: 'Asset'
  },
  {
    id: '3',
    glAccountNo: '1500',
    name: 'Equipment',
    shortName: 'Equipment',
    type: 'Asset'
  },
  {
    id: '4',
    glAccountNo: '5000',
    name: 'Cost of Goods Sold',
    shortName: 'COGS',
    type: 'Expense'
  },
  {
    id: '5',
    glAccountNo: '5100',
    name: 'Service Expenses',
    shortName: 'Service Exp',
    type: 'Expense'
  },
  {
    id: '6',
    glAccountNo: '5200',
    name: 'Equipment Expenses',
    shortName: 'Equipment Exp',
    type: 'Expense'
  },
  {
    id: '7',
    glAccountNo: '4000',
    name: 'Sales Revenue',
    shortName: 'Sales',
    type: 'Income'
  },
  {
    id: '8',
    glAccountNo: '4100',
    name: 'Service Revenue',
    shortName: 'Service Rev',
    type: 'Income'
  },
  {
    id: '9',
    glAccountNo: '4200',
    name: 'Equipment Revenue',
    shortName: 'Equipment Rev',
    type: 'Income'
  },
  {
    id: '10',
    glAccountNo: '2000',
    name: 'Accounts Payable',
    shortName: 'AP',
    type: 'Liability'
  }
];

const searchFieldOptions = ['Name', 'GL Account No', 'Short Name', 'Type'];

const GLAccountsModal: React.FC<GLAccountsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelect,
  title = "GL Account"
}) => {
  const [searchForm, setSearchForm] = useState<GLAccountSearchForm>(defaultSearchForm);
  const [glAccounts, setGlAccounts] = useState<GLAccountData[]>(mockGLAccounts);
  const [selectedGLAccount, setSelectedGLAccount] = useState<GLAccountData | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  // Calculate paginated data
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return glAccounts.slice(start, start + pageSize);
  }, [glAccounts, pageIndex, pageSize]);

  const handleSearchFieldChange = (field: keyof GLAccountSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    // Filter GL accounts based on search criteria
    const filtered = mockGLAccounts.filter(account => {
      let fieldValue = '';
      
      switch (searchForm.searchField) {
        case 'Name':
          fieldValue = account.name;
          break;
        case 'GL Account No':
          fieldValue = account.glAccountNo;
          break;
        case 'Short Name':
          fieldValue = account.shortName;
          break;
        case 'Type':
          fieldValue = account.type;
          break;
        default:
          fieldValue = account.name;
      }
      
      return fieldValue.toLowerCase().includes(searchForm.searchValue.toLowerCase());
    });
    setGlAccounts(filtered);
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setGlAccounts(mockGLAccounts);
    setSelectedGLAccount(null);
  };

  const handleRowClick = (account: GLAccountData) => {
    setSelectedGLAccount(account);
  };

  const handleRowDoubleClick = (account: GLAccountData) => {
    if (onSelect) {
      onSelect(account);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedGLAccount(null);
    onClose();
  };


  const handleClearSelection = () => {
    setSelectedGLAccount(null);
  };


  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'GL Account No',
      accessorKey: 'glAccountNo',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedGLAccount?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedGLAccount?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Short Name',
      accessorKey: 'shortName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedGLAccount?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Type',
      accessorKey: 'type',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedGLAccount?.id === cell.row.original.id ? 'table-active' : ''}
        >
          <span className={`badge ${
            cell.getValue() === 'Asset' ? 'bg-primary' :
            cell.getValue() === 'Expense' ? 'bg-danger' :
            cell.getValue() === 'Income' ? 'bg-success' :
            cell.getValue() === 'Liability' ? 'bg-warning' :
            'bg-secondary'
          }`}>
            {cell.getValue()}
          </span>
        </div>
      )
    }
  ], [selectedGLAccount]);

  // Pagination logic
  const pageCount = Math.ceil(glAccounts.length / pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;
  const pageOptions = Array.from({ length: pageCount }, (_, i) => i);

  const handlePreviousPage = () => {
    if (canPreviousPage) setPageIndex(pageIndex - 1);
  };
  const handleNextPage = () => {
    if (canNextPage) setPageIndex(pageIndex + 1);
  };
  const handleSetPage = (idx: number) => setPageIndex(idx);

  // Reset page index if data changes
  React.useEffect(() => {
    setPageIndex(0);
  }, [glAccounts, pageSize]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: true })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => {}}
              onKeyDown={() => {}}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={'1'} className="text-muted">
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className="me-3">
                  <button
                    onClick={handleSearch}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1}>
                  <button
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* GL Accounts Table */}
              <Row>
                <Col xs={12}>
                  <div style={{ height: '160px', overflowY: 'auto', width: '100%' }}>
                    <TableContainerReactTable
                      columns={columns}
                      data={paginatedData}
                      isGlobalFilter={false}
                      customPageSize={pageSize}
                      divClass="mb-0"
                      tableClass="table-centered align-middle table-nowrap mb-0"
                      theadClass="table-light"
                      thClass="px-3 py-2"
                      hidePagination={true}
                      verticalLines={true}
                    />
                  </div>
                </Col>
              </Row>
              {/* Custom Pagination below scrollable table */}
              <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <div className="col-sm">
                  <div className="text-muted">Showing <span className="fw-semibold ms-1">{pageSize}</span> of <span className="fw-semibold">{glAccounts.length}</span> Results</div>
                </div>
                <div className="col-sm-auto">
                  <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                    <li className={!canPreviousPage ? "page-item disabled" : "page-item"}>
                      <Link to="#" className="page-link" onClick={e => { e.preventDefault(); handlePreviousPage(); }}>Previous</Link>
                    </li>
                    {pageOptions.map((item, key) => (
                      <React.Fragment key={key}>
                        <li className="page-item">
                          <Link to="#" className={pageIndex === item ? "page-link active" : "page-link"} onClick={e => { e.preventDefault(); handleSetPage(item); }}>{item + 1}</Link>
                        </li>
                      </React.Fragment>
                    ))}
                    <li className={!canNextPage ? "page-item disabled" : "page-item"}>
                      <Link to="#" className="page-link" onClick={e => { e.preventDefault(); handleNextPage(); }}>Next</Link>
                    </li>
                  </ul>
                </div>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <button
          onClick={handleClearSelection}
          className="btn btn-soft-warning btn-sm"
          style={{ minWidth: 80 }}
        >
          Clear
        </button>
        <button
          className="btn btn-soft-dark btn-sm"
          onClick={handleCancel}
          style={{ minWidth: 80 }}
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default GLAccountsModal;
